import { useContext } from 'react';
import styled from '@emotion/styled';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useNavigation } from '@hotjar/navigation/src/hooks/useNavigation';
import { Badge, Button, Card, Icon, Stack, Text, tokens } from '@hotjar/hotsauce';

import { LegacyContext } from '../../context/global-context';
import { integrationGatesOpen } from '../../utils';
import type { Integration } from '../../types';

const CardBox = styled(Card)`
  display: flex;
  flex-direction: column;
  width: 240px;
  height: 220px;
  padding: ${tokens.spacer.x2};
  border-radius: ${tokens.radius.l};

  ${tokens.transition('box-shadow', 200)};
  cursor: pointer;
  ${tokens.elevation(1)};
  &:hover {
    ${tokens.elevation(3)};
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${tokens.spacer.x1};
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: ${tokens.spacer.x1};
`;

const Name = styled(Text)`
  margin-bottom: 0;
  flex: 1;
`;

const Description = styled(Text)`
  display: flex;
  flex: 1;
  margin-bottom: ${tokens.spacer.x1};
`;

const CTAButton = styled(Button)`
  display: flex;
  justify-content: center;
`;

const Bold = styled.span`
  font-weight: bold;
`;

const ClickableWrapper = styled(Link)`
  width: 100%;
  height: 100%;
  cursor: pointer;

  &:hover {
    text-decoration: none;
  }
`;

const NameWrapper = styled(Stack)`
  align-items: center;
`;

interface IntegrationCardProps {
  integration: Integration;
  enabled: boolean;
  hideButton?: boolean;
  siteId: number;
}

export const IntegrationCard = ({
  integration,
  enabled,
  hideButton,
  siteId,
}: IntegrationCardProps): JSX.Element => {
  const { t } = useTranslation();
  const { buildHrefFromRoute } = useNavigation();
  const { routes = { integrationDetails: '' } } = useContext(LegacyContext);
  const canUse = integrationGatesOpen(integration);

  const getButtonText = () => {
    if (!enabled) {
      return t('integrations:card.set-up');
    }

    return t('integrations:card.how-to-use-it');
  };

  return (
    <ClickableWrapper
      to={{
        pathname: buildHrefFromRoute(routes.integrationDetails, {
          siteId,
          integrationName: integration.slug,
        }),
      }}
    >
      <CardBox>
        <CardHeader>
          <Icon icon={integration.icon} size="l" />
          <Wrapper>
            <NameWrapper spacing="x1">
              <Name as="p" textType="subtitle1">
                {t(integration.name)}
              </Name>
              {integration.isBeta && <Badge kind="beta" size="s" />}
            </NameWrapper>
            <Text textType="caption" textColor="text_light">
              {`${t('integrations:card.by')} ${t(integration.provider)}`}
            </Text>
          </Wrapper>
        </CardHeader>
        <Description textColor="text_light">{t(integration.description)}</Description>
        {!hideButton &&
          (canUse ? (
            <CTAButton aria-label={getButtonText()} size="s" kind={enabled ? 'ghost' : 'secondary'}>
              {getButtonText()}
            </CTAButton>
          ) : (
            <Text textType="subtitle3">
              {integration.tier && (
                <Trans
                  i18nKey={`integrations:gate-closed.${integration.tier}`}
                  components={[<Bold />]}
                />
              )}
            </Text>
          ))}
      </CardBox>
    </ClickableWrapper>
  );
};
